import React, {useLayoutEffect, useRef} from "react";
import { useDispatch } from "react-redux";
import {isSmWidth} from "../../../Apis/functions";
import TableFunction from "../../table/TableFunction";

/**
 * @Author: karen Yunuen Pozos Arias
 * @JobTitle : Full Stack Developer - IBL
 * @Email : kpozos@ibl.mx
 *
 * Hook-like component that handles assigned items table logic,
 * including pagination, selection, bulk actions, and dynamic data fetching.
 *
 * @param {Object} params - Configuration parameters
 * @param {string} params.keyId - Unique identifier key for each item
 * @param {Object} params.results - Data source containing assigned items and pagination info
 * @param {Function} params.setResults - Updates the results state
 * @param {string} params.searchElement - Current search input value
 * @param {string} params.labelRowsPerPage - Label for rows per page in pagination
 * @param {Function} params.tableBuildBody - Function to build the request body for API calls
 * @param {Function} params.tableEndPoint - Endpoint function to fetch table data
 * @param {string} params.viewTypeName - View type identifier for rendering variations
 * @param {boolean} params.modified - Indicates if the data has been modified
 * @param {Function} params.setModified - Updates the modified state
 * @param {Function} params.setIsAllChecked - Updates the "select all" checkbox state
 * @param {Array} params.selected - List of selected items
 * @param {Function} params.setSelected - Updates selected items list
 * @param {number} [params.bottom=60] - Bottom offset used to compute the "no data" container height
 *
 * @returns {Object} Table handlers and state
 * @returns {Function} returns.getData - Function to fetch paginated data
 * @returns {Object} returns.pagination - Pagination configuration
 * @returns {number} returns.rowsPerPage - Number of rows per page
 * @returns {React.RefObject} returns.elementRef - Reference for dynamic height calculation
 * @returns {React.RefObject} returns.notInformationRef - Reference for "no data" container height adjustment
 * @returns {Function} returns.handleDeleteAll - Handler to remove all selected items
 * @returns {Function} returns.handleCheckboxChange - Handler for checkbox selection changes
 * @returns {Function} returns.t - Translation function
 */
const AssignedItemsTableApp = (
    {
        keyId,
        results,
        setResults,
        searchElement,
        labelRowsPerPage,
        tableBuildBody,
        tableEndPoint,
        viewTypeName,
        modified,
        setModified,
        setIsAllChecked,
        selected,
        setSelected,
        autoHeight,
        bottom = 60
    }
) => {
    const dispatch = useDispatch();
    const elementRef = useRef(null);
    const notInformationRef = useRef(null);

    useLayoutEffect(() => {
        if (isSmWidth() || !autoHeight) return;

        /**
         * Distance from the top of the page to the element, whatever has been scrolled. Measuring
         * only from the viewport made the list taller each time it was mounted on a scrolled page.
         */
        const getTop = (element) => {
            let scrollTop = window.scrollY;
            for (let parent = element.parentElement; parent; parent = parent.parentElement) {
                if (/(auto|scroll)/.test(getComputedStyle(parent).overflowY) && parent.scrollHeight > parent.clientHeight) {
                    scrollTop = parent.scrollTop;
                    break;
                }
            }
            return element.getBoundingClientRect().top + scrollTop;
        };

        const updateHeight = () => {
            if (elementRef.current) {
                const distanceTop = getTop(elementRef.current) + 40;
                elementRef.current.style.setProperty('--top-value', `${distanceTop}px`);
            }

            if (notInformationRef.current) {
                // minHeight instead of height: the card fills the remaining viewport
                // but never shrinks below its own content, so the legend stays inside.
                notInformationRef.current.style.height = 'auto';
                notInformationRef.current.style.minHeight = `${window.innerHeight - (getTop(notInformationRef.current) + bottom)}px`;
            }
        };

        updateHeight();
        window.addEventListener('resize', updateHeight);
        return () => window.removeEventListener('resize', updateHeight);
    }, [results?.content]);

    /**
     * Executes a paginated search request using basic criteria.
     *
     * Builds the request body with the current page, page size, selected model,
     * and any additional criteria passed as arguments.
     * Triggers the search action through Redux and updates the results on success.
     */
    const getData = (page, { searchElement }) => {
        dispatch(tableEndPoint(tableBuildBody({page, rowsPerPage, searchElement }))).then(({ payload: { data: { data, success } } }) => {
                if (success === true) {
                    setResults(data);
                }
            },
        );
    };
    /**
     * Handle to build table
     */
    const { t, pagination, rowsPerPage, handleDeleteAll, handleCheckboxChange } = TableFunction({
        data: results,
        labelRowsPerPage: labelRowsPerPage,
        hasContainer: true,
        fixedPagination: false,
        setIsAllChecked,
        searchElement, //New
        getData,//New
        view: viewTypeName,
        modified,
        setModified,
        selectedList: selected,
        setSelectedList: setSelected,
        keyId
    });

    return {
        t,
        pagination,
        rowsPerPage,
        elementRef,
        notInformationRef,
        getData,
        handleDeleteAll,
        handleCheckboxChange
    };

}

export default AssignedItemsTableApp;
